import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { db, auth } from '../../firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import {
  FiUser, FiUsers, FiAward, FiFilter, FiCheck, FiX,
  FiChevronRight, FiCalendar, FiTarget, FiBarChart2,
  FiChevronDown, FiChevronUp, FiTrash2, FiAlertTriangle
} from 'react-icons/fi';

// Kardok ikon (Swords) SVG
const IconSwords = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="14.5 17.5 3 5 3 3 5 3 17.5 14.5" />
    <line x1="13" y1="19" x2="19" y2="13" />
    <line x1="16" y1="16" x2="20" y2="20" />
    <line x1="19" y1="21" x2="21" y2="19" />
    <polyline points="9.5 17.5 21 5 21 3 19 3 6.5 14.5" />
    <line x1="11" y1="19" x2="5" y2="13" />
  </svg>
);

const monthNames = [
  "", "Január", "Február", "Március", "Április", "Május", "Június",
  "Július", "Augusztus", "Szeptember", "Október", "November", "December"
];

const bowShortLabels = {
  '3d_hdh_trrb': 'TRRB',
  '3d_hdh_bb': 'BB',
  '3d_hdh_cu': 'CU',
  '3d_hdh_hu': 'HU',
  '3d_hdh_pbhb': 'PBHB',
  '3d_hdh_trlb': 'TRLB',
  '3d_hdh_ol': 'OL',
  '3d_hdh_crb': 'CRB',
  '3d_hdh_bg': 'BG',
  '3d_hdh_cb': 'CB',
  'tortenelmi_ij': 'Tört.íj',
  'orom_tortenelmi_ij': 'Tört.íj'
};

const formatBowCategory = (rawCat) => {
  if (!rawCat) return '';
  if (bowShortLabels[rawCat]) return bowShortLabels[rawCat];
  if (rawCat.includes('_')) return rawCat.split('_').pop().toUpperCase();
  return rawCat.toUpperCase();
};

const MyWorkouts = () => {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_my_workouts');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_my_workouts');
      return !(cached && JSON.parse(cached).length > 0);
    } catch (e) {
      return true;
    }
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [filters, setFilters] = useState({
    solo: true,
    group: true,
    rival: true,
    competition: true
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // --- ÚJ ÁLLAPOTOK A TÖRLÉSHEZ ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState(null);

  // Kinyitott szekciók állapota (localStorage-ból töltve)
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('myworkouts_expanded_v1');
    return saved ? JSON.parse(saved) : {}; // Alapból minden csukva, kivéve amit a logika kinyit
  });

  useEffect(() => {
    localStorage.setItem('myworkouts_expanded_v1', JSON.stringify(expandedSections));
  }, [expandedSections]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        let myworkoutsList = [];
        let legacyWorkoutsList = [];

        const updateMergedWorkouts = () => {
          const map = new Map();
          // Összefűzzük mind a két mappát ID alapján - 0% duplikáció!
          myworkoutsList.forEach(item => map.set(item.id, item));
          legacyWorkoutsList.forEach(item => map.set(item.id, item));

          const merged = Array.from(map.values());

          // Rendezés létrehozás dátuma szerint csökkenőbe
          merged.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB - dateA;
          });

          setWorkouts(merged);
          setLoading(false);

          // Helyi mentés localStorage-ba az azonnali 0 ms-os betöltésért legközelebb
          try {
            localStorage.setItem('cached_my_workouts', JSON.stringify(merged));
          } catch (e) {
            console.warn("Nem sikerült elmenteni az edzés cache-t:", e);
          }
        };

        const q1 = query(
          collection(db, 'users', user.uid, 'myworkouts'),
          orderBy('createdAt', 'desc')
        );

        const q2 = query(
          collection(db, 'users', user.uid, 'workouts'),
          orderBy('createdAt', 'desc')
        );

        const unsubSnap1 = onSnapshot(q1, (snapshot) => {
          myworkoutsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          updateMergedWorkouts();
        }, (error) => {
          console.warn("Hiba myworkouts betöltésekor:", error);
          setLoading(false);
        });

        const unsubSnap2 = onSnapshot(q2, (snapshot) => {
          legacyWorkoutsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          updateMergedWorkouts();
        }, (error) => {
          console.warn("Hiba workouts betöltésekor:", error);
          setLoading(false);
        });

        return () => {
          unsubSnap1();
          unsubSnap2();
        };
      } else {
        setWorkouts([]);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const toggleFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleExpand = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // --- TÖRLÉSI LOGIKA ---
  const openDeleteConfirm = (e, workout) => {
    e.stopPropagation();
    setWorkoutToDelete(workout);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!workoutToDelete || !currentUser) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'myworkouts', workoutToDelete.id)).catch(() => {});
      await deleteDoc(doc(db, 'users', currentUser.uid, 'workouts', workoutToDelete.id)).catch(() => {});
      setShowDeleteModal(false);
      setWorkoutToDelete(null);
    } catch (error) {
      console.error("Hiba a törlés során:", error);
      alert("Hiba történt a törlés közben.");
    }
  };

  const getWorkoutStyle = (type) => {
    switch (type) {
      case 'group': return { color: '#2196F3', icon: <FiUsers />, label: 'Csoportos' };
      case 'rival': return { color: '#E53935', icon: <IconSwords />, label: 'Párbaj' };
      case 'competition': return { color: '#FFD700', icon: <FiAward />, label: 'Verseny' };
      default: return { color: '#4CAF50', icon: <FiUser />, label: 'Egyéni' };
    }
  };

  // Csoportosítás évek és hónapok szerint
  const groupedWorkouts = useMemo(() => {
    const filtered = workouts.filter(w => filters[w.type || 'solo']);
    const groups = {};

    filtered.forEach(w => {
      const date = w.createdAt?.toDate ? w.createdAt.toDate() : new Date(w.createdAt);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;

      if (!groups[year]) groups[year] = {};
      if (!groups[year][month]) groups[year][month] = [];
      groups[year][month].push(w);
    });

    return groups;
  }, [workouts, filters]);

  if (loading) return <div style={centerStyle}>Edzések betöltése... 🏹</div>;
  if (!currentUser) return <div style={centerStyle}>Kérlek jelentkezz be az edzéseid megtekintéséhez!</div>;

  return (
    <div style={containerStyle} className="force-light-theme">
      {/* FEJLÉC ÉS SZŰRŐ */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>Edzéseim</h2>
        <button style={filterBtnStyle} onClick={() => setShowFilterPanel(!showFilterPanel)}>
          <FiFilter /> Szűrés
        </button>
      </div>

      {showFilterPanel && (
        <div style={filterPanelStyle}>
          {Object.keys(filters).map(key => {
            const style = getWorkoutStyle(key);
            return (
              <label key={key} style={filterItemStyle}>
                <input
                  type="checkbox"
                  checked={filters[key]}
                  onChange={() => toggleFilter(key)}
                  style={{ display: 'none' }}
                />
                <div style={checkboxStyle(filters[key], style.color)}>
                  {filters[key] ? <FiCheck size={14} /> : <FiX size={14} />}
                </div>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{style.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* CSOPORTOSÍTOTT LISTA */}
      <div style={listContainerStyle}>
        {Object.keys(groupedWorkouts).length > 0 ? (
          Object.keys(groupedWorkouts).sort((a, b) => b - a).map(year => {
            const isYearExpanded = expandedSections[`year_${year}`] !== false; // Alapból true
            return (
              <div key={year} style={yearSectionStyle}>
                {/* ÉV SÁV */}
                <div
                  style={yearHeaderStyle}
                  onClick={() => toggleExpand(`year_${year}`)}
                >
                  <span style={{ fontSize: '18px', fontWeight: '900' }}>{year}</span>
                  {isYearExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </div>

                {isYearExpanded && Object.keys(groupedWorkouts[year]).sort((a, b) => b - a).map(month => {
                  const isMonthExpanded = expandedSections[`month_${year}_${month}`] !== false; // Alapból true
                  const monthWorkouts = groupedWorkouts[year][month];

                  return (
                    <div key={month} style={monthSectionStyle}>
                      {/* HÓNAP SÁV */}
                      <div
                        style={monthHeaderStyle}
                        onClick={() => toggleExpand(`month_${year}_${month}`)}
                      >
                        <span style={{ fontWeight: 'bold' }}>{monthNames[month]}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={countBadgeStyle}>{monthWorkouts.length} db</span>
                          {isMonthExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                        </div>
                      </div>

                      {/* EDZÉSEK A HÓNAPBAN */}
                      {isMonthExpanded && (
                        <div style={cardsGridStyle}>
                          {monthWorkouts.map(workout => {
                            const style = getWorkoutStyle(workout.type);
                            const date = workout.createdAt?.toDate ? workout.createdAt.toDate() : new Date(workout.createdAt);

                            // --- DINAMIKUS ADATOK KIKERESÉSE CSOPORTOS EDZÉSHEZ ---
                            let displayScore = workout.totalScore || 0;
                            let displayAverage = workout.averageArrowScore || 0;

                            if (workout.type === 'group' && workout.participants) {
                              const me = workout.participants.find(p => p.uid === currentUser?.uid);
                              if (me) {
                                displayScore = me.totalScore;
                                displayAverage = me.average;
                              }
                            }

                            return (
                              <div
                                key={workout.id}
                                style={cardStyle}
                                onClick={() => {
                                  if (sessionStorage.getItem('selecting_workout_for_share') === 'true') {
                                    sessionStorage.removeItem('selecting_workout_for_share');
                                    sessionStorage.setItem('selected_workout_to_share', JSON.stringify(workout));
                                    navigate('/kozosseg');
                                  } else {
                                    navigate(`/edzes/${workout.id}`);
                                  }
                                }}
                              >
                                <div style={{ ...indicatorStyle, backgroundColor: style.color }}>
                                  <div style={iconWrapperStyle}>{style.icon}</div>
                                </div>
                                <div style={cardContentStyle}>
                                  <div style={cardHeaderRow}>
                                    <h4 style={cardNameStyle}>{workout.workoutName || 'Névtelen edzés'}</h4>
                                    {workout.bowCategory && (
                                       <span style={catBadgeStyle}>{formatBowCategory(workout.bowCategory)}</span>
                                    )}
                                  </div>
                                  <div style={cardInfoRow}>
                                    <FiCalendar size={12} style={{ marginRight: '4px' }} />
                                    {date.toLocaleDateString('hu-HU')} {date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                  <div style={cardStatsRow}>
                                    <div style={statItemStyle}>
                                      <FiTarget size={14} color={style.color} style={{ marginRight: '4px' }} />
                                      <strong>{displayScore}</strong> pont
                                    </div>
                                    <div style={statItemStyle}>
                                      <FiBarChart2 size={14} color={style.color} style={{ marginRight: '4px' }} />
                                      Átlag: <strong>{displayAverage?.toFixed(2) || '0.00'}</strong>
                                    </div>
                                  </div>
                                </div>

                                {/* NARANCSSÁRGA TÖRLÉS SAROK */}
                                <div
                                  style={deleteCornerStyle}
                                  onClick={(e) => openDeleteConfirm(e, workout)}
                                >
                                  <FiTrash2 size={14} color="#fff" style={{ position: 'absolute', bottom: '10px', right: '4px' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        ) : (
          <div style={emptyTextStyle}>Nincs megjeleníthető edzés.</div>
        )}
      </div>

      <div style={{ height: '80px' }} />

      {/* --- TÖRLÉS MEGERŐSÍTŐ MODAL (PORTAL) --- */}
      {showDeleteModal && createPortal(
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle} className="force-light-theme">
            <FiAlertTriangle size={50} color="#b30000" style={{ marginBottom: '15px' }} />
            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Edzés törlése</h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5', fontSize: '15px' }}>
              Biztosan törölni szeretnéd a(z) <br/>
              <strong style={{ color: '#333' }}>{workoutToDelete?.workoutName || 'választott'}</strong> <br/>
              nevű bejegyzést? Ez a művelet végleges.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                style={modalCancelButtonStyle}
                onClick={() => {
                  setShowDeleteModal(false);
                  setWorkoutToDelete(null);
                }}
              >
                Mégse
              </button>
              <button
                style={modalDeleteButtonStyle}
                onClick={handleDelete}
              >
                Igen, törlöm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// --- STÍLUSOK ---
const containerStyle = {
  padding: '15px',
  maxWidth: '600px',
  margin: '0 auto',
  fontFamily: 'Arial, sans-serif',
  backgroundColor: '#f8f9fa',
  minHeight: '100vh'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '15px'
};

const titleStyle = { margin: 0, color: '#333', fontSize: '22px', fontWeight: 'bold' };

const filterBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
  backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '15px',
  cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: '#666'
};

const filterPanelStyle = {
  backgroundColor: '#fff', borderRadius: '10px', padding: '12px',
  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px',
  marginBottom: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', border: '1px solid #eee'
};

const filterItemStyle = { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' };

const checkboxStyle = (active, color) => ({
  width: '18px', height: '18px', borderRadius: '5px', border: `2px solid ${color}`,
  backgroundColor: active ? color : 'transparent', display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: '#fff'
});

const listContainerStyle = { display: 'flex', flexDirection: 'column', gap: '15px' };

const yearSectionStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };

const yearHeaderStyle = {
  backgroundColor: '#b30000', color: '#fff', padding: '10px 15px',
  borderRadius: '8px', display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
};

const monthSectionStyle = { display: 'flex', flexDirection: 'column', gap: '5px', marginLeft: '5px' };

const monthHeaderStyle = {
  backgroundColor: '#fff', color: '#b30000', padding: '8px 12px',
  borderRadius: '6px', display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', cursor: 'pointer', border: '1px solid #eee',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
};

const countBadgeStyle = {
  backgroundColor: '#f1f1f1', color: '#666', padding: '2px 8px',
  borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
};

const cardsGridStyle = { display: 'flex', flexDirection: 'column', gap: '8px', padding: '5px 0' };

const cardStyle = {
  backgroundColor: '#fff', borderRadius: '10px', display: 'flex',
  overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  cursor: 'pointer', border: '1px solid #eee', position: 'relative'
};

const indicatorStyle = { width: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const iconWrapperStyle = { color: '#fff', fontSize: '18px', display: 'flex' };

const cardContentStyle = { flex: 1, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '2px' };
const cardHeaderRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' };
const cardNameStyle = { margin: 0, fontSize: '15px', color: '#333', fontWeight: 'bold', maxWidth: '80%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const cardInfoRow = { fontSize: '11px', color: '#888', display: 'flex', alignItems: 'center' };
const cardStatsRow = { display: 'flex', gap: '15px', marginTop: '3px' };
const statItemStyle = { fontSize: '13px', color: '#555', display: 'flex', alignItems: 'center' };
const catBadgeStyle = { backgroundColor: '#f0f0f0', color: '#666', padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold' };
const chevronStyle = { display: 'flex', alignItems: 'center', paddingRight: '8px' };

const deleteCornerStyle = {
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: '45px',
  height: '45px',
  backgroundColor: '#E65100',
  clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
  cursor: 'pointer',
  zIndex: 10
};

const emptyTextStyle = { textAlign: 'center', padding: '30px', color: '#999', fontStyle: 'italic' };

// --- MODAL STÍLUSOK ---
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 3000,
  backdropFilter: 'blur(3px)'
};

const modalContentStyle = {
  backgroundColor: '#ffffff',
  padding: '25px 20px',
  borderRadius: '15px',
  textAlign: 'center',
  width: '320px',
  maxWidth: '90vw',
  boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
  boxSizing: 'border-box',
  zIndex: 3001,
  color: '#333333'
};

const modalDeleteButtonStyle = {
  backgroundColor: '#b30000',
  color: 'white',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const modalCancelButtonStyle = {
  backgroundColor: '#eee',
  color: '#333',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const centerStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#666' };

export default MyWorkouts;
